import { createContext, useContext, useState, ReactNode, useCallback } from "react";

export type NotificationTone = "success" | "critical" | "warning" | "info";

export interface Notification {
  id: string;
  message: string;
  tone: NotificationTone;
  title?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  showNotification: (message: string, tone: NotificationTone, title?: string) => void;
  dismissNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((message: string, tone: NotificationTone, title?: string) => {
    const id = `notification-${Date.now()}-${Math.random()}`;
    const notification: Notification = { id, message, tone, title };

    setNotifications((prev) => [...prev, notification]);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, showNotification, dismissNotification }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
}
